import { api, ApiError } from "./api";

/**
 * Two-step confirm for operations that send real email or change something big (docs/CONTRACTS.md
 * "Confirm tokens"): the first call goes without confirm and the server answers confirm_required with
 * its preview and a confirm_token; the person reads the preview, and the second call sends
 * confirm=true with that token. If the preview changed in between, the server asks again.
 */
export type AskFirst<T> = { done: true; result: T } | { done: false; preview: unknown; token?: string; message: string };

/** Calls path with body (no confirm). Returns the server's preview when it asks first, or the result when it didn't need to. */
export async function askFirst<T>(path: string, body: Record<string, unknown>, method?: string): Promise<AskFirst<T>> {
  try {
    return { done: true, result: await api<T>(path, { method, json: body }) };
  } catch (e) {
    if (e instanceof ApiError && e.code === "confirm_required") return { done: false, preview: e.details, token: e.confirmToken, message: e.message };
    throw e;
  }
}

/** The confirming call: the same body plus confirm=true and the token from the preview. */
export async function confirmWith<T>(path: string, body: Record<string, unknown>, token?: string, method?: string): Promise<AskFirst<T>> {
  return askFirst<T>(path, { ...body, confirm: true, ...(token ? { confirm_token: token } : {}) }, method);
}

/** What automations' go-live, start and move previews carry (internal/automations/live_confirm.go LiveConfirm). */
export interface LiveConfirm {
  action: string;
  automation_id: number;
  automation: string;
  status_now: string;
  version?: number;
  triggers?: string[];
  email_steps: number;
  first_email_subject?: string;
  held_real_runs?: number;
  contacts?: number;
  archived_skipped?: number;
  start_step?: string;
}
